/**
 * Thin client for the sell-back endpoints (`design/economy/economy-rebuild.md` §6,
 * `backend/src/game/sellback/`).
 *
 * Two calls, deliberately never merged into one round trip: `getSellbackQuote` is read-only and
 * safe to call just to find out a price, `sellItemBack` performs the sale. The Inventory tile
 * always fetches a fresh quote right before it asks the player to confirm — the payout is 50%
 * (admin-tunable, `gem_sellback_bps`) of what THAT PURCHASE actually cost, recovered from the
 * account's own ledger, never a number computed in the client.
 *
 * The response carries no `payoutCurrency` field on purpose: `SellbackService.sell` never
 * converts currency (crediting Gems for a Credits purchase would be a free route to the scarce
 * currency), so the payout is always in `paidCurrency`. One field, not two, to match what the
 * server actually sends rather than a shape that only looks tidier.
 */
import { api } from '../../core/network/api';

export type SellbackCurrency = 'SOFT' | 'PREMIUM';

export interface SellbackQuote {
  itemId: string;
  /** The `CurrencyLedger` DEBIT row this quote is computed from. */
  purchaseRef: string;
  paidAmount: number;
  paidCurrency: SellbackCurrency;
  /** Basis points of `paidAmount` the sale pays out — 5000 = 50%. */
  rateBps: number;
  /** Paid out in `paidCurrency` — see the module doc for why there is no separate currency
   * field for it. */
  payoutAmount: number;
}

/** Read-only preview — what selling `itemId` back would pay right now. Never mutates. */
export const getSellbackQuote = (itemId: string): Promise<SellbackQuote> =>
  api.get<SellbackQuote>(`/sellback/quote/${encodeURIComponent(itemId)}`);

/** Sells `itemId` back at the currently quoted rate. Returns the same shape as the quote, so a
 * caller that already has one open does not need a second read to know what was actually paid. */
export const sellItemBack = (itemId: string): Promise<SellbackQuote> =>
  api.post<SellbackQuote>(`/sellback/${encodeURIComponent(itemId)}`);

/**
 * Bulk quote — one round trip for a whole Sell page.
 *
 * The page shows every owned item's real payout, and a payout is a ledger lookup per item, so
 * the single-item route would be forty requests to draw one screen. An item that cannot be sold
 * comes back as `ok: false` with its reason rather than throwing, because one granted item must
 * not cost the caller the other thirty-nine quotes.
 */
export type BulkQuote =
  | ({ ok: true } & SellbackQuote)
  | { ok: false; itemId: string; reason: string };

/**
 * The server's own caps, mirrored here because THIS is the layer that has to respect them.
 *
 * `QuoteBulkDto` allows 100 ids, `SellBulkDto` 50 — the quote is a read, a sale mutates. Both
 * reject the whole request when the list is longer, and the Sell page quotes every item the
 * account owns, which on a long-lived account is not a number anyone gets to assume. A seeded
 * dev player owns 295: the page sent all of them, the route answered 400, and the page fell
 * back to "no quotes at all" — which it then rendered as every row being sellable at a payout
 * of zero, so the sale went out and the server refused every line of it ("жмакал продать, но
 * продажа не работала" — Arturs, 2026-09-21). Chunking lives here rather than in the page for
 * the same reason the caps do: a caller should not have to know a route's batch size.
 */
export const SELLBACK_QUOTE_BATCH = 100;
export const SELLBACK_SELL_BATCH = 50;

function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export const getSellbackQuotes = async (itemIds: readonly string[]): Promise<{ quotes: BulkQuote[] }> => {
  const batches = chunk(itemIds, SELLBACK_QUOTE_BATCH);
  // `allSettled`, not `all`: one bad batch must cost its own hundred rows their payout, not the
  // other two hundred theirs. The page already draws a quote-less row as unsellable-with-reason.
  const settled = await Promise.allSettled(
    batches.map((ids) => api.post<{ quotes: BulkQuote[] }>('/sellback/quotes', { itemIds: ids })),
  );
  const quotes: BulkQuote[] = [];
  for (const result of settled) if (result.status === 'fulfilled') quotes.push(...result.value.quotes);
  // Every batch failed: that is a real failure and the caller must hear about it rather than
  // read an empty array as "nothing is sellable".
  if (quotes.length === 0 && batches.length > 0) {
    const first = settled.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
    throw (first?.reason as Error | undefined) ?? new Error('sellback quote failed');
  }
  return { quotes };
};

export type BulkSellResult =
  | { ok: true; itemId: string; payoutAmount: number; paidCurrency: SellbackCurrency }
  | { ok: false; itemId: string; reason: string };

/**
 * Sells several items in one request.
 *
 * Partial success is a real outcome and the response says which: each item keeps its own
 * idempotency key server-side, so one refusal never rolls back the sales that already paid out.
 * The caller must render per-item results rather than a single "done".
 */
export const sellItemsBack = async (
  itemIds: readonly string[],
): Promise<{ results: BulkSellResult[]; totals: Record<SellbackCurrency, number> }> => {
  const results: BulkSellResult[] = [];
  const totals: Record<SellbackCurrency, number> = { SOFT: 0, PREMIUM: 0 };
  // Sequential, unlike the quotes: each batch takes a wallet lock per item server-side, and
  // firing them in parallel would only queue behind each other with a worse failure story.
  // Select-all on an account that owns three hundred things has to work, so the cap is this
  // module's problem and not a limit on what the page may offer.
  for (const ids of chunk(itemIds, SELLBACK_SELL_BATCH)) {
    const batch = await api.post<{ results: BulkSellResult[]; totals: Record<SellbackCurrency, number> }>(
      '/sellback/bulk',
      { itemIds: ids },
    );
    results.push(...batch.results);
    totals.SOFT += batch.totals.SOFT ?? 0;
    totals.PREMIUM += batch.totals.PREMIUM ?? 0;
  }
  return { results, totals };
};
